import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { unsubscribeEducation, requestUnsubscribeLink } from "@/lib/leadMagnet.functions";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/unsubscribe")({
  head: () => ({
    meta: [
      { title: "Unsubscribe — Currency Thoughts" },
      {
        name: "description",
        content:
          "Manage your email preferences for educational resources from Currency Thoughts LLC.",
      },
      { property: "og:title", content: "Unsubscribe — Currency Thoughts" },
      {
        property: "og:description",
        content: "Manage your email preferences for Currency Thoughts educational resources.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  validateSearch: (s: Record<string, unknown>) => ({
    email: typeof s["email"] === "string" ? s["email"] : "",
    token: typeof s["token"] === "string" ? s["token"] : "",
  }),
  component: UnsubscribePage,
});

function UnsubscribePage() {
  const { email, token } = Route.useSearch();
  const run = useServerFn(unsubscribeEducation);
  const requestLink = useServerFn(requestUnsubscribeLink);
  const hasToken = Boolean(email && token);
  const [state, setState] = useState<"working" | "done" | "error" | "form">(
    hasToken ? "working" : "form",
  );
  const [message, setMessage] = useState("");
  const [entered, setEntered] = useState("");
  const [website, setWebsite] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!hasToken) return;
    (async () => {
      try {
        await run({ data: { email, token } });
        setState("done");
      } catch (e) {
        setState("error");
        setMessage(e instanceof Error ? e.message : "We couldn't process that link.");
      }
    })();
  }, [email, token, hasToken]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSending(true);
    try {
      await requestLink({ data: { email: entered.trim().toLowerCase(), website } });
    } catch {
      // Response is intentionally identical either way.
    }
    setSending(false);
    setSent(true);
  }

  return (
    <div className="flex min-h-screen flex-col bg-cream-50">
      <Navbar />
      <main className="flex-1 pt-20">
        <section className="mx-auto max-w-2xl px-5 py-20 text-center md:px-10">
          <div className="flex justify-center">
            <Eyebrow>Email Preferences</Eyebrow>
          </div>
          <h1 className="mt-5 font-display text-[34px] leading-tight text-navy-900 md:text-[44px]">
            {state === "form"
              ? sent
                ? "Check your inbox."
                : "Unsubscribe from emails"
              : state === "working"
              ? "Updating your preferences…"
              : state === "done"
                ? "You've been unsubscribed."
                : "We couldn't complete that."}
          </h1>
          <p className="mt-4 font-sans text-[16px] leading-relaxed text-charcoal-500">
            {state === "form"
              ? sent
                ? "If that address is on our list, we've sent a confirmation link. Click the link in that email to complete your unsubscribe."
                : "Enter your email address and we'll send you a confirmation link. Clicking that link completes the unsubscribe."
              : state === "done"
              ? `${email} will no longer receive educational emails from Currency Thoughts. Any active policy or account notices will still be sent.`
              : state === "error"
                ? message
                : "One moment."}
          </p>

          {state === "form" && !sent ? (
            <form onSubmit={onSubmit} className="mx-auto mt-8 flex max-w-md flex-col gap-3">
              <Input
                type="email"
                required
                autoCapitalize="none"
                autoCorrect="off"
                placeholder="you@example.com"
                value={entered}
                onChange={(e) => setEntered(e.target.value)}
                aria-label="Email address"
              />
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="hidden"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
              <Button type="submit" disabled={sending}>
                {sending ? "Sending…" : "Send confirmation link"}
              </Button>
              <p className="font-sans text-[12px] leading-relaxed text-charcoal-500">
                Prefer email? Write to currencythoughtsllc@gmail.com and we'll remove you.
              </p>
            </form>
          ) : null}

          {state === "error" ? (
            <button
              type="button"
              onClick={() => {
                setState("form");
                setSent(false);
              }}
              className="mt-6 border-b border-bronze-600/40 font-sans text-[12px] uppercase tracking-[0.18em] text-bronze-600"
            >
              Request a new link
            </button>
          ) : null}
          <Link
            to="/academy/library"
            className="mt-8 inline-block border-b border-bronze-600/40 font-sans text-[12px] uppercase tracking-[0.18em] text-bronze-600"
          >
            Back to the Family Library
          </Link>
        </section>
      </main>
      <Footer />
    </div>
  );
}